import { apiRequest } from '../../api/client';

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

/** Loại người thực hiện, khớp `AuditActorType` bên BE. */
export type AuditActorType = 'Member' | 'Staff' | 'System';

/** Một dòng nhật ký cho màn `a-audit` (S9-BE-01, AC-26). */
export interface AuditLogItem {
  id: string;
  actorType: AuditActorType;
  actorId: string | null;
  /** Tên người thực hiện đã tra ra; null với dòng hệ thống (không có actor). */
  actorName: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  branchId: string | null;
  branchName: string | null;
  /** JSONB nguyên văn trước khi đổi; null khi tạo mới. */
  before: string | null;
  /** JSONB nguyên văn sau khi đổi; null khi xóa. */
  after: string | null;
  correlationId: string | null;
  createdAt: string;
}

export interface AuditLogResponse {
  items: AuditLogItem[];
  limit: number;
  /** Con trỏ trang kế; null khi hết dòng. */
  nextCursor: string | null;
}

/** Bộ lọc audit viewer. Ngày theo lịch VN (yyyy-MM-dd). */
export interface AuditLogFilters {
  actorType?: AuditActorType;
  /** Lọc theo NHÓM thao tác bằng tiền tố (`config.`, `loyalty.`…). Nhiều tiền tố = OR. */
  actionPrefixes?: string[];
  entity?: string;
  branchId?: string;
  from?: string;
  to?: string;
}

/**
 * Nhóm thao tác cho ô lọc — mỗi nhóm gom một hoặc nhiều tiền tố `action` có thật trong hệ thống.
 * "Cấu hình" chính là "config log viewer" mà mô tả task nhắc tới (BR-802): đổi cấu hình ghi vào
 * `audit_logs` với `config.changed`/`config.reset`, không có bảng riêng.
 */
export interface AuditActionGroup {
  value: string;
  label: string;
  prefixes: string[];
}

export const AUDIT_ACTION_GROUPS: AuditActionGroup[] = [
  { value: 'all', label: 'Tất cả thao tác', prefixes: [] },
  { value: 'config', label: 'Cấu hình', prefixes: ['config.'] },
  { value: 'rules', label: 'Quy tắc & hạng', prefixes: ['loyalty.'] },
  { value: 'points', label: 'Điểm, quét & đảo', prefixes: ['bill_scan.', 'reversal.', 'voucher.'] },
  { value: 'marketing', label: 'Khuyến mãi, quà & nội dung', prefixes: ['campaign.', 'reward.', 'content.'] },
  { value: 'member', label: 'Thành viên & quyền riêng tư', prefixes: ['member.', 'privacy.'] },
  { value: 'staff', label: 'Nhân viên & ca làm', prefixes: ['staff.'] },
  {
    value: 'catalog',
    label: 'Chi nhánh & danh mục',
    prefixes: ['branch.', 'chain.', 'bank_account.', 'pos_store_code.', 'reservation_slot.'],
  },
  { value: 'reporting', label: 'Đối soát & báo cáo', prefixes: ['reporting.'] },
];

export const auditKeys = {
  list: (filters: AuditLogFilters, cursor: string | null) =>
    ['audit', 'logs', filters, cursor ?? 'first'] as const,
};

export const auditApi = {
  /** Danh sách audit log — phân trang bằng con trỏ keyset (S9-DB-01). */
  list: (filters: AuditLogFilters, cursor?: string | null, limit = 20) => {
    const params = new URLSearchParams();
    if (filters.actorType) params.set('actorType', filters.actorType);
    (filters.actionPrefixes ?? []).forEach((p) => params.append('actionPrefix', p));
    if (filters.entity) params.set('entity', filters.entity);
    if (filters.branchId) params.set('branchId', filters.branchId);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (cursor) params.set('cursor', cursor);
    params.set('limit', String(limit));
    return unwrap(apiRequest<AuditLogResponse>(`/admin/audit-logs?${params}`));
  },
};
